import { Badge } from "@/components/ui/badge"
import { CurrencyIcon } from "@/components/ui/CurrencyIcon"
import { Input } from "@/components/ui/input"
import { Heading, Text } from "@/components/ui/typography"
import { coverageFacts } from "@/content/facts"
import { sfIcons } from '@/lib/sf-icons'
import { SUPPORTED_COUNTRIES } from "@/lib/supportedCountries"
import { cn } from "@/lib/utils"
import { semanticTokens } from "@supabank/config/theme"
import { useMemo, useState } from "react"
import { CircleFlag } from "react-circle-flags"
import { useTranslation } from "react-i18next"

/**
 * Network Brand Colors (Official Brand Guidelines)
 * @see https://ethereum.org/en/assets/ (Ethereum)
 * @see https://solana.com/branding (Solana)
 * @see https://polygon.technology/media-kit (Polygon)
 * @see https://docs.arbitrum.io/for-devs/concepts/public-chains (Arbitrum)
 * @see https://community.optimism.io/docs/biz/brand/ (Optimism)
 * @see https://docs.base.org/brand-kit (Base)
 * @see https://www.avax.network/brand (Avalanche)
 * @see https://www.stellar.org/press (Stellar)
 * @see https://tron.network/resources (Tron)
 */
const NETWORK_BRAND_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  Ethereum: {
    bg: "bg-[#627EEA]/20",
    border: "border-[#627EEA]/40",
    text: "text-[#A4B8FF]", // Lightened for dark bg contrast
  },
  Solana: {
    bg: "bg-[#14F195]/15",
    border: "border-[#14F195]/35",
    text: "text-[#14F195]",
  },
  Polygon: {
    bg: "bg-[#8247E5]/20",
    border: "border-[#8247E5]/40",
    text: "text-[#B794F6]", // Lightened for dark bg contrast
  },
  Arbitrum: {
    bg: "bg-[#28A0F0]/20",
    border: "border-[#28A0F0]/40",
    text: "text-[#5BC0FF]", // Lightened for dark bg contrast
  },
  Optimism: {
    bg: "bg-[#FF0420]/15",
    border: "border-[#FF0420]/35",
    text: "text-[#FF6B7A]", // Lightened for dark bg contrast
  },
  Base: {
    bg: "bg-[#0052FF]/20",
    border: "border-[#0052FF]/40",
    text: "text-[#6B9FFF]", // Lightened for dark bg contrast
  },
  Avalanche: {
    bg: "bg-[#E84142]/15",
    border: "border-[#E84142]/35",
    text: "text-[#FF7A7B]", // Lightened for dark bg contrast
  },
  Stellar: {
    bg: "bg-white/15",
    border: "border-white/30",
    text: "text-white",
  },
  Tron: {
    bg: "bg-[#FF0000]/15",
    border: "border-[#FF0000]/35",
    text: "text-[#FF6B6B]", // Lightened for dark bg contrast
  },
  Bitcoin: {
    bg: "bg-[#F7931A]/15",
    border: "border-[#F7931A]/35",
    text: "text-[#FFB347]", // Lightened for dark bg contrast
  },
}

/** Get brand colors for a network, with fallback to neutral */
const getNetworkColors = (network: string) => {
  return NETWORK_BRAND_COLORS[network] || {
    bg: "bg-white/15",
    border: "border-white/20",
    text: "text-white",
  }
}

export type GeoCoverageProps = { id?: string }

export function GeoCoverage({ id }: GeoCoverageProps) {
  const { t } = useTranslation("marketing")
  const [search, setSearch] = useState("")
  const [showAll, setShowAll] = useState(false)

  // Top Corridors from SSOT (marketing.json)
  const topCorridors = ((t("geoCoverage.topCorridors", { returnObjects: true }) as any[]) || []).map((corridor: any) => ({
    code: corridor.code,
    name: corridor.name,
    currency: corridor.currency,
    rails: corridor.rails || [],
    badge: corridor.badge
  }))

  const filteredCountries = useMemo(() => {
    if (!search) return []
    const normalized = search.trim().toLowerCase()
    return SUPPORTED_COUNTRIES.filter((country) =>
      country.code.toLowerCase().includes(normalized) || country.name.toLowerCase().includes(normalized)
    )
  }, [search])

  return (
    <section 
      id={id} 
      aria-labelledby="geo-coverage-title" 
      className={cn("relative py-16 sm:py-24 overflow-hidden", "outline-none", semanticTokens.bg.canvas)}
      tabIndex={-1}
    >
      {/* Subtle gradient overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-50 dark:opacity-30"
        style={{
          background: `
            radial-gradient(ellipse at 50% 0%, hsla(217, 91%, 60%, 0.08) 0%, transparent 60%)
          `
        }}
      />
      <div className="relative z-10 mx-auto max-w-6xl px-4 space-y-12">

        {/* Header & Search */}
        <div className="space-y-8 text-center">
          <div className="space-y-4 max-w-3xl mx-auto">
            <Text variant="caption" color="muted" className="uppercase tracking-widest font-semibold">
              {t("geoCoverage.badge")}
            </Text>
            <Heading level={2} id="geo-coverage-title">
              {t("geoCoverage.headline")} <br />
              <span className={cn(semanticTokens.fg.muted)}>{t("geoCoverage.headlineSubtext")}</span>
            </Heading>
          </div>

          {/* Centralized Search Engine Liquid Glass Style */}
          <div className="max-w-2xl mx-auto relative z-10">
            <div className={cn(
              "relative group rounded-full transition-all",
              semanticTokens.motion.standard,
              "focus-within:shadow-lg focus-within:-translate-y-0.5"
            )}>
              <sfIcons.search className={cn("absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 z-10", semanticTokens.fg.muted)} />
              <Input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("geoCoverage.searchPlaceholder")}
                className={cn(
                  "pl-14 h-16 rounded-full",
                  semanticTokens.typography.bodyLarge,
                  // Liquid Glass style: subtle border, soft background
                  "bg-white/80 dark:bg-slate-900/80",
                  "backdrop-blur-sm",
                  "border border-slate-200/60 dark:border-slate-700/60",
                  // Canon focus style
                  "focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:border-primary/40",
                  "transition-all duration-240"
                )}
              />
            </div>
          </div>
        </div>

        {/* Dynamic Content Area */}
        <div className="min-h-[400px]">
          {search ? (
            /* Search Results Mode */
            <div className="space-y-4 max-w-3xl mx-auto">
              {filteredCountries.length > 0 ? (
                filteredCountries.map(country => (
                  <div
                    key={country.code}
                    className={cn(
                      "rounded-xl p-5",
                      semanticTokens.bg.surface,
                      "border border-border/50",
                      "shadow-sm",
                      "transition-all duration-200",
                      "hover:shadow-md hover:border-border/80"
                    )}
                  >
                    <div className="flex flex-col md:flex-row gap-5 md:items-center justify-between">
                      {/* Destination Info Flag + Name */}
                      <div className="flex items-center gap-4 min-w-0 md:min-w-[200px]">
                        <div className="w-12 h-12 rounded-full overflow-hidden shrink-0 shadow-sm">
                          <CircleFlag countryCode={country.code.toLowerCase()} height={48} width={48} />
                        </div>
                        <div>
                          <Heading level={3}>{country.name}</Heading>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="bg-muted/30 border-border/50">
                              {country.currencies.join(" / ")}
                            </Badge>
                            {country.status === 'active' ? (
                              <span className={cn("flex items-center gap-1 text-xs font-medium", semanticTokens.positive.accent)}>
                                <sfIcons.checkCircle2 className="w-3.5 h-3.5" /> Active
                              </span>
                            ) : (
                              <span className={cn("flex items-center gap-1 text-xs font-medium", semanticTokens.warning.fg)}>
                                <div className="w-2 h-2 rounded-full bg-amber-500" /> {country.status}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Bridge Visual */}
                      <div className="hidden md:flex flex-col items-center justify-center px-4 opacity-20">
                        <div className="flex gap-1 mb-1">
                          <div className={cn("w-1 h-1 rounded-full", semanticTokens.bg.canvas.replace("bg-", "bg-foreground/"))} />
                          <div className={cn("w-1 h-1 rounded-full", semanticTokens.bg.canvas.replace("bg-", "bg-foreground/"))} />
                          <div className={cn("w-1 h-1 rounded-full", semanticTokens.bg.canvas.replace("bg-", "bg-foreground/"))} />
                        </div>
                        <sfIcons.next className={cn("w-5 h-5", semanticTokens.fg.default)} />
                      </div>

                      {/* Rails & Capabilities */}
                      <div className="flex-1 space-y-3">
                        <div>
                          <p className={cn("text-[11px] uppercase tracking-wide font-semibold mb-2", semanticTokens.fg.muted)}>
                            {t("geoCoverage.labels.payoutRails")}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {country.rails.map(rail => (
                              <span
                                key={rail}
                                className={cn(
                                  "px-2.5 py-1 rounded-md",
                                  "text-xs font-medium tracking-wide",
                                  "bg-muted/50 text-muted-foreground",
                                  "border border-border/30"
                                )}
                              >
                                {rail}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div className={cn("pt-3 border-t", semanticTokens.border.subtle)}>
                          <p className={cn("text-[11px] uppercase tracking-wide font-semibold mb-2", semanticTokens.fg.muted)}>
                            {t("geoCoverage.labels.fundWith")}
                          </p>
                          <div className="flex flex-wrap gap-2 items-center">
                            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200/50 dark:border-blue-800/50">
                              <CurrencyIcon symbol="usdc" size={18} />
                              <span className="text-xs font-medium text-blue-700 dark:text-blue-400">USDC</span>
                            </div>
                            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200/50 dark:border-emerald-800/50">
                              <CurrencyIcon symbol="usdt" size={18} />
                              <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">USDT</span>
                            </div>
                            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted/50 border border-border/50">
                              <CurrencyIcon symbol="eth" size={18} />
                              <span className="text-xs font-medium text-muted-foreground">ETH</span>
                            </div>
                            <span className="text-xs text-muted-foreground/70">+6 more</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 rounded-2xl border border-dashed border-border bg-muted/50">
                  <sfIcons.error className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
                  <Text variant="body" className={cn(semanticTokens.typography.label, semanticTokens.fg.muted)}>
                    {t("geoCoverage.search.noResults", { query: search })}
                  </Text>
                  <Text variant="sm" className={cn(semanticTokens.fg.muted, "opacity-70 mt-1")}>
                    {t("geoCoverage.search.noResultsHint")}
                  </Text>
                </div>
              )}
            </div>
          ) : (
            /* Default Mode: Popular Corridors Grid Clean Surface Cards */
            <div className="grid grid-cols-1 gap-4 sm:gap-5 md:gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {topCorridors.map((corridor) => (
                <div
                  key={corridor.code}
                  className={cn(
                    "p-5 rounded-xl",
                    // Level 1 Surface clean card styling
                    semanticTokens.bg.surface,
                    "border border-border/50",
                    "shadow-sm",
                    // Hover effect
                    "transition-all duration-200",
                    "hover:shadow-md hover:border-border/80",
                    "hover:-translate-y-0.5"
                  )}
                >
                  {/* Header: Flag + Currency + Badge */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden ring-1 ring-border/30">
                        <CurrencyIcon symbol={corridor.currency} size={40} />
                      </div>
                      <div>
                        <span className={cn(semanticTokens.typography.label, "text-lg font-semibold")}>
                          {corridor.currency}
                        </span>
                        <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                          {corridor.name}
                        </p>
                      </div>
                    </div>
                    {corridor.badge && (
                      <Badge variant="outline" className={cn(
                        semanticTokens.info.bg, semanticTokens.info.fg, semanticTokens.info.border,
                        "text-[10px] font-medium px-1.5 py-0.5"
                      )}>
                        {corridor.badge}
                      </Badge>
                    )}
                  </div>

                  {/* Payment Rails Clean Text Badges */}
                  <div className="flex flex-wrap items-center gap-1.5 pt-3 border-t border-border/30">
                    {corridor.rails.map(rail => (
                      <span
                        key={rail}
                        className={cn(
                          "px-2 py-0.5 rounded",
                          "text-[11px] font-medium",
                          semanticTokens.fg.muted,
                          semanticTokens.bg.muted
                        )}
                      >
                        {rail}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer: Multichain Support (Always visible, anchors the section) */}
        <div className={cn(
          "relative overflow-hidden rounded-xl sm:rounded-2xl border",
          "p-6 sm:p-8 md:p-10",
          semanticTokens.premium.bgGradient, "text-white shadow-xl"
        )}>
          <div className={cn(
            "absolute top-0 right-0 -mt-10 -mr-10 w-64 h-64 rounded-full blur-3xl",
            semanticTokens.accent.primary.replace("bg-", "bg-") + "/20"
          )} />
          <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl" />

          <div className="relative z-10 flex flex-col md:flex-row gap-6 sm:gap-7 md:gap-8 items-center justify-between">
            <div className="space-y-3 sm:space-y-4 max-w-xl text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-2 sm:gap-3 mb-2">
                {/* WCAG AA fix: blue-300 on dark bg = ~7:1 contrast ratio */}
                <sfIcons.network className="w-4 h-4 sm:w-5 sm:h-5 text-blue-300" />
                <Text as="span" variant="caption" className="font-bold tracking-wide uppercase sm:text-sm text-blue-300">
                  {t("geoCoverage.multichain.badge")}
                </Text>
              </div>
              {/* CANON: Display responsive for hero text */}
              <Heading level={2} className="text-white">
                {t("geoCoverage.multichain.headline")}
              </Heading>
              {/* CANON: Body large for lead text - WCAG AA: slate-200 on dark = ~10:1 */}
              <Text variant="lg" className="text-slate-200 leading-relaxed">
                {t("geoCoverage.multichain.description")}
              </Text>
            </div>

            <div className="flex flex-wrap justify-center gap-2 max-w-sm">
              {Array.from(new Set(coverageFacts.stablecoins.flatMap(s => s.networks))).slice(0, 8).map(network => {
                const colors = getNetworkColors(network)
                return (
                  <Badge
                    key={network}
                    className={cn(
                      // Brand colors for each network (subtle tint style)
                      colors.bg,
                      colors.border,
                      colors.text,
                      "hover:opacity-90",
                      "px-2 sm:px-3 py-1 sm:py-1.5 backdrop-blur-sm transition-all",
                      semanticTokens.typography.captionSmall,
                      "sm:text-sm font-medium"
                    )}
                  >
                    {network}
                  </Badge>
                )
              })}
              <Badge className={cn(
                // Neutral style for "+N more" badge
                "bg-white/10 border-white/15 text-slate-300",
                semanticTokens.typography.captionSmall,
                "sm:text-sm"
              )}>{t("geoCoverage.multichain.more")}</Badge>
            </div>
          </div>
        </div>

        <div className="text-center pt-6 sm:pt-8 border-t border-border/50">
          <div className={cn(
            "flex flex-wrap items-center justify-center gap-4 sm:gap-6 md:gap-8",
            semanticTokens.typography.captionSmall,
            "sm:text-sm font-medium",
            semanticTokens.fg.muted
          )}>
            <span>{t("geoCoverage.stats.countries")}</span>
            <span className="w-1 h-1 rounded-full bg-muted-foreground/30 hidden sm:inline" />
            <span>{t("geoCoverage.stats.stablecoins")}</span>
            <span className="w-1 h-1 rounded-full bg-muted-foreground/30 hidden sm:inline" />
            <span>{t("geoCoverage.stats.settlement")}</span>
          </div>
          <Text variant="caption-sm" className={cn(
            "sm:text-sm mt-3 sm:mt-4 max-w-2xl mx-auto",
            semanticTokens.fg.muted
          )}>
            {t("geoCoverage.disclaimer")}
          </Text>
        </div>

      </div>
    </section>
  )
}
